import { Injectable } from "@angular/core";
import { poe2 } from "../../../game";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Rarity, Comparator, GearSizeType } from "../../models/enums";
import { Filter, CustomRule, AdvancedConditions, DisplayOptions } from "../../models/filter-models";
import { RuleGeneratorService } from "./rule-generator.service";

@Injectable({
  providedIn: 'root'
})
export class RecipeSalvagingGeneratorService {
  constructor(private ruleFormatter: RuleGeneratorService) {
  }

  public generateJewellersRecipeExceptionsRules(filter: Filter): string {
    if (poe2 || !filter.showJewellersRecipe) return '';

    return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearSalvagingRecipeJewellers, new CustomRule({
      advancedConditions: new AdvancedConditions({ minSockets: 6 }),
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual,
    }), filter, 'Jeweller\'s Recipe');
  }

  public generateChromaticRecipeExceptionsRules(filter: Filter): string {
    if (poe2 || !filter.showChromaticRecipe) return '';

    const baseRule = new CustomRule({
      advancedConditions: new AdvancedConditions({ socketGroup: 'RGB' }),
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual,
    });

    switch (filter.showChromaticRecipeType) {
      case GearSizeType.All:
        return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearSalvagingRecipeChromatic, baseRule, filter, 'Chromatic Recipe');

      case GearSizeType.MediumOnly:
        return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearSalvagingRecipeChromatic, baseRule, filter, 'Medium Chromatic Recipe', {
          customLine: '\n  Width <= 2\n  Height <= 3',
        });

      case GearSizeType.SmallOnly:
        return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearSalvagingRecipeChromatic, baseRule, filter, 'Small Chromatic Recipe', {
          customLine: '\n  Width <= 2\n  Height <= 2',
        }) + '\n\n' +
        this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearSalvagingRecipeChromatic, baseRule, filter, 'Small Long Chromatic Recipe', {
          customLine: '\n  Width == 1\n  Height <= 4',
        });

      case GearSizeType.TwoByTwoOnly:
        return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearSalvagingRecipeChromatic, baseRule, filter, 'Two-by-Two Chromatic Recipe', {
          customLine: '\n  Width <= 2\n  Height <= 2',
        });

      default:
        return '';
    }
  }

  public generateChaosRecipeExceptionsRules(filter: Filter): string {
    if (!filter.showChaosRecipe) {
      return '';
    }

    const rule = new CustomRule({
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Rare,
      displayOptions: new DisplayOptions(),
    });

    return this.ruleFormatter.generateRule(rule, filter, 'gear-recipe-chaos', 'Chaos Recipe', {
      customClass: this.buildItemClass(filter.showChaosRecipeTarget),
      customLine: `
  ItemLevel >= 60
  ItemLevel <= 74`
    });
  }

  public generateExaltRecipeExceptionsRules(filter: Filter): string {
    if (!filter.showExaltRecipe) {
      return '';
    }

    const rule = new CustomRule({
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Rare,
      displayOptions: new DisplayOptions(),
      advancedConditions: new AdvancedConditions({
        hasInfluence: true,
        influences: ['All'],
      }),
    });

    return this.ruleFormatter.generateRule(rule, filter, 'gear-recipe-exalt', 'Exalt Recipe', {
      customClass: this.buildItemClass(filter.showExaltRecipeTarget),
    });
  }

  buildItemClass(showChaosRecipeTarget: string[]): string | undefined {
    let itemClass = '';
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Helmets')) {
      itemClass += '"Helmets" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Gloves')) {
      itemClass += '"Gloves" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Boots')) {
      itemClass += '"Boots" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Body Armours')) {
      itemClass += '"Body Armours" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Two Hand Weapons')) {
      itemClass += '"Bows" "Two Hand Axes" "Two Hand Maces" "Two Hand Swords" "Warstaves" "Staves" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('One Hand Weapons')) {
      itemClass += '"Daggers" "Rune Daggers" "Wands" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Jewellery')) {
      itemClass += '"Rings" "Amulets" "Belts" ';
    }

    return itemClass.trim();
  }
}
